import { baseApi } from '@shared/api/baseApi.js';
import { normalizeDebitCard } from '@shared/lib/utils';

export const userApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getCurrentUser: builder.query({
			query: () => '/v1/user',
			// debitCards defaults to [] rather than staying undefined so consumers can treat
			// it as a list against a backend that does not send it yet.
			transformResponse: (response) => ({
				...response,
				debitCard: normalizeDebitCard(response?.debitCard),
				debitCards: (response?.debitCards ?? []).map(normalizeDebitCard),
			}),
			providesTags: ['User'],
		}),
		// No invalidatesTags: callers patch the getCurrentUser cache optimistically and
		// invalidate 'User' themselves in finally, per the optimistic update pattern.
		patchCurrentUser: builder.mutation({
			query: (body) => ({
				url: '/v1/user',
				method: 'PATCH',
				body,
			}),
		}),
	}),
});

export const { useGetCurrentUserQuery, usePatchCurrentUserMutation } = userApi;

// PATCH /v1/user
//
// Auth Required
// Update the user's default benefit and/or language. card-service normalizes the language to
// one the platform supports (de, en, es; anything else becomes en) and syncs it to DiPocket
// and cidaas.
//
// Request body (at least one property):
//   {
//     "defaultBenefitConfigId": { "type": "string", "format": "uuid" },
//     "language": { "type": "string", "minLength": 2, "maxLength": 10 }
//   }
//
// Responses:
//   200 — { "defaultBenefitConfigId": "uuid", "defaultBenefitConfig": "string", "language": "de" }
//   404 — user, card limit or card not found

// GET /v1/user
//
// Auth Required
// Get user details and more.
//
// Responses:
//   200 — OK
//   {
//     "type": "object",
//     "properties": {
//       "id": { "type": "string", "format": "uuid", "description": "The id of the user" },
//       "employeeId": { "type": ["string", "null"], "description": "The external employee id, if set" },
//       "employeeEntryDate": { "type": ["string", "null"], "format": "date-time", "description": "The employee entry date, if set" },
//       "firstName": { "type": "string" },
//       "lastName": { "type": "string" },
//       "email": { "type": "string" },
//       "phone": { "type": "string" },
//       "birthDate": { "type": "string", "format": "date" },
//       "benefitPostalCode": { "type": "string", "description": "The postal code used to determine the user's benefits" },
//       "defaultBenefitConfig": { "type": "string", "description": "The name of the user's default benefit configuration" },
//       "defaultBenefitConfigId": { "type": "string", "format": "uuid", "description": "The id of the user's default benefit configuration" },
//       "benefitAccountMembershipStatus": { "type": ["string", "null"], "description": "The membership status of the benefit account" },
//       "status": { "type": "string", "enum": ["created", "invited", "verified", "identified"], "description": "The status of the user" },
//       "language": { "type": "string", "description": "The user's preferred language code" },
//       "companyId": { "type": "string", "format": "uuid", "description": "The id of the company the user belongs to" },
//       "companyName": { "type": "string", "description": "The name of the company the user belongs to" },
//       "companyUser": { "type": "boolean", "description": "Is the user a company user" },
//       "companyUsers": { "type": "array", "description": "The list of companies the user is a company user for", "items": { "type": "object" } },
//       "balances": {
//         "type": "array",
//         "items": {
//           "type": "object",
//           "properties": {
//             "cardId": { "type": "string", "description": "The id of the benefit card" },
//             "cardBalance": { "type": "integer", "description": "The balance of the card in cents" },
//             "cardNetwork": { "type": "string", "description": "The network/region of the card" }
//           }
//         }
//       },
//       "transactions": { "type": "array", "description": "The user's recent transactions", "items": { "type": "object" } },
//       "benefitCards": {
//         "type": "array",
//         "items": {
//           "type": "object",
//           "properties": {
//             "id": { "type": "string", "description": "The id of the benefit card" },
//             "default": { "type": "string", "description": "The id of the default benefit card" },
//             "benefitImageUrl": { "type": "string", "description": "The image url of the benefit" },
//             "type": { "type": "string", "description": "The type of the benefit" },
//             "value": { "type": "integer", "description": "The value of the benefit in cents" },
//             "network": { "type": "string", "description": "The network/region of the benefit" },
//             "vouchersValue": { "type": "integer", "description": "The voucher value in cents" },
//             "name": { "type": "string", "description": "The name of the benefit" },
//             "postalCodePrefix": { "type": "string", "description": "Comma separated postal code prefixes covered by the benefit" },
//             "createdAt": { "type": "string", "format": "date-time", "description": "When the benefit card was created" }
//           }
//         }
//       },
//       "debitCard": {
//         "type": ["object", "null"],
//         "description": "The primary card: the account's oldest issued card. Null when the account has no issued card.",
//         "properties": {
//           "id": { "type": "string", "format": "uuid", "description": "The id of the debitcard" },
//           "type": { "type": "string", "enum": ["Virtual", "Physical", "VirtualAndPhysical"] },
//           "cardImageUrl": { "type": ["string", "null"] },
//           "cardMaskedNumber": { "type": ["string", "null"] },
//           "cardExpDate": { "type": ["string", "null"] },
//           "cardStatus": { "type": "string", "description": "Casing varies by issuer: DiPocket sends 'active'/'ordered'/'canceled', Swan sends 'Enabled'/'Canceled'/'ConsentPending', and 'Frozen' is synthesized from the isFrozen flag" }
//         }
//       },
//       "debitCards": {
//         "type": "array",
//         "description": "Every debit card of the account, same six fields per entry as debitCard above. The primary card comes first, the rest follow oldest first. NOT filtered — cancelled and consent-pending cards are included, and so are cards issued while the user was at a previous employer (the query is not scoped by companyId). cardStatus tells the client which ones are usable. Absent on backends before card-service #1322.",
//         "items": { "type": "object" }
//       }
//     }
//   }
//
// Example response:
//   {
//     "id": "123e4567-e89b-12d3-a456-426614174000",
//     "employeeId": null,
//     "employeeEntryDate": null,
//     "firstName": "string",
//     "lastName": "string",
//     "email": "string",
//     "phone": "string",
//     "birthDate": "2026-07-16",
//     "benefitPostalCode": "string",
//     "defaultBenefitConfig": "string",
//     "defaultBenefitConfigId": "123e4567-e89b-12d3-a456-426614174000",
//     "benefitAccountMembershipStatus": null,
//     "status": "created",
//     "language": "string",
//     "companyId": "123e4567-e89b-12d3-a456-426614174000",
//     "companyName": "string",
//     "companyUser": true,
//     "companyUsers": [{}],
//     "balances": [{ "cardId": "string", "cardBalance": 1, "cardNetwork": "string" }],
//     "transactions": [{}],
//     "benefitCards": [
//       {
//         "id": "string",
//         "default": "string",
//         "benefitImageUrl": "string",
//         "type": "string",
//         "value": 1,
//         "network": "string",
//         "vouchersValue": 1,
//         "name": "string",
//         "postalCodePrefix": "string",
//         "createdAt": "2026-07-16T12:49:09.344Z"
//       }
//     ],
//     "debitCard": {
//       "id": "123e4567-e89b-12d3-a456-426614174000",
//       "type": "Virtual",
//       "cardImageUrl": "string",
//       "cardMaskedNumber": "string",
//       "cardExpDate": "string",
//       "cardStatus": "active"
//     },
//     "debitCards": [
//       {
//         "id": "123e4567-e89b-12d3-a456-426614174000",
//         "type": "Virtual",
//         "cardImageUrl": "string",
//         "cardMaskedNumber": "string",
//         "cardExpDate": "string",
//         "cardStatus": "active"
//       },
//       {
//         "id": "223e4567-e89b-12d3-a456-426614174001",
//         "type": "Physical",
//         "cardImageUrl": "string",
//         "cardMaskedNumber": "string",
//         "cardExpDate": "string",
//         "cardStatus": "Frozen"
//       }
//     ]
//   }
//
